import { createTheme } from "@mui/material/styles";

/**
 * Brent & Audrey brand palette. These seven are the whole vocabulary — every
 * colour below is one of them, so anything added later has a name to reuse
 * rather than a new hex to invent.
 */
export const brand = {
  linen: "#FFFFFF",
  fawn: "#D5D3C6",
  wildSage: "#B2BAA6",
  eucalyptus: "#74878B",
  silverFir: "#475353",
  camel: "#CFA35B",
  castIron: "#2D2D2C",
  /** The three background shades that replace the old light greys. */
  shellLight: "#F2F1ED",
  shellMid: "#E8E6E0",
  shellDeep: "#D5D3C6",
} as const;

// Hand-picked tints/shades of Camel and Wild Sage. MUI would derive these from
// `main` on its own, but its derivation desaturates towards grey and the brand
// tones are what the hover and text states need to stay on.
const camelLight = "#DFBE87";
const camelDark = "#A87F3C";
const sageDark = "#7E8B72";

export const theme = createTheme({
  palette: {
    // Silver Fir carries the app bar, drawer header and every structural
    // accent, exactly as the header mock in the style guide shows it.
    primary: {
      main: brand.silverFir,
      light: "#6B7676",
      dark: "#2F3939",
      contrastText: brand.linen,
    },
    // Camel is the brandmark colour and the call-to-action colour.
    secondary: {
      main: brand.camel,
      light: camelLight,
      dark: camelDark,
      contrastText: brand.castIron,
    },
    // "At location" / "At other location" badges — the guide pairs Wild Sage
    // with Camel for exactly that distinction.
    success: { main: brand.wildSage, dark: sageDark, contrastText: brand.castIron },
    info: { main: brand.eucalyptus, contrastText: brand.linen },
    warning: { main: brand.camel, dark: camelDark, contrastText: brand.castIron },
    background: { default: brand.shellLight, paper: brand.linen },
    text: { primary: brand.castIron, secondary: brand.eucalyptus },
    divider: brand.wildSage,
    // Only the light end of the grey ramp is rebranded: those are the steps the
    // UI uses as surfaces, and the guide replaces the light grey with these.
    // The dark end stays neutral so it keeps working as plain ink.
    grey: {
      50: "#F8F7F4",
      100: brand.shellLight,
      200: brand.shellMid,
      300: brand.shellDeep,
    },
  },
  typography: {
    fontFamily: "Roboto, -apple-system, BlinkMacSystemFont, \"Segoe UI\", sans-serif",
  },
  components: {
    MuiAppBar: {
      defaultProps: { elevation: 0, color: "primary" },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        // The guide's filled button is Camel with Cast Iron text. Text and
        // outlined buttons keep Silver Fir, so secondary actions stay quiet.
        containedPrimary: ({ theme }) => ({
          backgroundColor: theme.palette.secondary.main,
          color: theme.palette.secondary.contrastText,
          "&:hover": { backgroundColor: theme.palette.secondary.dark },
        }),
      },
    },
    MuiTextField: {
      defaultProps: { variant: "standard" },
    },
  },
});
